import React from 'react';
import { createTheme } from '@mui/material';

// Design system per DESIGN.md — a warm-cream/coral/serif-display identity (Cormorant Garamond
// substitutes for the licensed Copernicus/Tiempos Headline display face; Inter substitutes for
// StyreneB body sans — both loaded via Google Fonts in public/index.html).
//
// IMPORTANT — the display/title split is unbreakable per DESIGN.md: only the four *display*
// sizes (display-xl/lg/md/sm — hero headlines, section heads) are serif. The three *title*
// sizes (title-lg/md/sm — card titles, dialog titles, list labels) stay sans, just bigger/bolder
// than body text. Mapping ALL of h1-h6 to serif (an earlier pass's mistake) is wrong: h5/h6 are
// title-lg/title-md, not display sizes.
//
// Every DESIGN.md typography token has a named MUI variant below (see the module augmentation),
// so future font/size changes happen in ONE place (this object) instead of hunting down every
// ad-hoc `sx={{ fontSize, fontWeight }}` Typography across the app. Native h1-h6/body1/body2/
// caption/overline/button also stay configured (existing `variant="h5"` etc. call sites keep
// working), but new headline code should reach for the named variants (`variant="displayLg"`)
// so it's unambiguous which DESIGN.md token it maps to.
const SERIF_DISPLAY = '"Cormorant Garamond", "EB Garamond", Garamond, serif';
const SANS_BODY = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const MONO_CODE = '"JetBrains Mono", ui-monospace, monospace';

export const DARK_PALETTE = {
  surface: '#111e2d',
  elevated: '#1c2d40',
  hover: '#263b50',
  text: '#f6f7f2',
  muted: '#b7c5d2',
  accent: '#81cdbb',
  blue: '#9fbce8',
  blueSoft: '#b8cee8',
  teal: '#5aab9b',
  accentOnLight: '#28675d',
  blueOnLight: '#395b85',
};

// Design token layer — the color equivalent of the typography variants below. Every hex value
// DESIGN.md names lives here ONCE, keyed by DESIGN.md's own token name (kebab-case there, camelCase
// here). A future DESIGN.md swap means editing this object only: (1) MUI's own palette slots
// (primary/error/text/background/etc., needed so built-in MUI components like <Button> pick it up
// automatically) are derived from it below, and (2) everything DESIGN.md names that MUI has no slot
// for (canvas vs surface-card vs surface-soft, hairlines, dark-mode surfaces, muted-soft, etc.) is
// exposed verbatim as `theme.palette.tokens.<name>` — see the module augmentation — so components
// reference a token name instead of inlining hex in `sx`.
export const DESIGN_TOKENS = {
  primary: '#cc785c',
  primaryActive: '#a9583e',
  primaryDisabled: '#e6dfd8',
  ink: '#141413',
  body: '#3d3d3a',
  bodyStrong: '#252523',
  muted: '#6c6a64',
  mutedSoft: '#8e8b82',
  hairline: '#e6dfd8',
  hairlineSoft: '#ebe6df',
  canvas: '#faf9f5',
  surfaceSoft: '#f5f0e8',
  surfaceCard: '#efe9de',
  surfaceCreamStrong: '#e8e0d2',
  surfaceDark: DARK_PALETTE.surface,
  surfaceDarkElevated: DARK_PALETTE.elevated,
  surfaceDarkSoft: DARK_PALETTE.hover,
  onPrimary: '#ffffff',
  // Same value as onPrimary today, but a DIFFERENT concept: onPrimary means specifically "text on
  // the coral primary button" per DESIGN.md. `white` means "this surface/text is plain white" with
  // no relation to the primary color — a plain white card, a white chip label on an arbitrary
  // category color, a white spinner. Keeping them separate means changing what "primary button
  // text" looks like can never accidentally recolor an unrelated white card, and vice versa.
  white: '#ffffff',
  onDark: DARK_PALETTE.text,
  onDarkSoft: DARK_PALETTE.muted,
  accentOnDark: DARK_PALETTE.accent,
  accentTeal: '#5db8a6',
  accentTealDark: '#368575',
  accentAmber: '#e8a55a',
  success: '#5db872',
  warning: '#d4a017',
  error: '#c64545',
} as const;

declare module '@mui/material/styles' {
  interface Palette {
    tokens: typeof DESIGN_TOKENS;
  }
  interface PaletteOptions {
    tokens: typeof DESIGN_TOKENS;
  }
  interface TypographyVariants {
    displayXl: React.CSSProperties;
    displayLg: React.CSSProperties;
    displayMd: React.CSSProperties;
    displaySm: React.CSSProperties;
    titleLg: React.CSSProperties;
    titleMd: React.CSSProperties;
    titleSm: React.CSSProperties;
    bodyMd: React.CSSProperties;
    bodySm: React.CSSProperties;
    captionUppercase: React.CSSProperties;
    code: React.CSSProperties;
    navLink: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    displayXl?: React.CSSProperties;
    displayLg?: React.CSSProperties;
    displayMd?: React.CSSProperties;
    displaySm?: React.CSSProperties;
    titleLg?: React.CSSProperties;
    titleMd?: React.CSSProperties;
    titleSm?: React.CSSProperties;
    bodyMd?: React.CSSProperties;
    bodySm?: React.CSSProperties;
    captionUppercase?: React.CSSProperties;
    code?: React.CSSProperties;
    navLink?: React.CSSProperties;
  }
}
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    displayXl: true;
    displayLg: true;
    displayMd: true;
    displaySm: true;
    titleLg: true;
    titleMd: true;
    titleSm: true;
    bodyMd: true;
    bodySm: true;
    captionUppercase: true;
    code: true;
    navLink: true;
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: DESIGN_TOKENS.primary,
      dark: DESIGN_TOKENS.primaryActive,
      light: DESIGN_TOKENS.primaryDisabled,
    },
    secondary: {
      main: DESIGN_TOKENS.accentTeal,
    },
    error: {
      main: DESIGN_TOKENS.error,
    },
    warning: {
      main: DESIGN_TOKENS.warning,
    },
    success: {
      main: DESIGN_TOKENS.success,
    },
    text: {
      primary: DESIGN_TOKENS.ink,
      secondary: DESIGN_TOKENS.muted,
    },
    background: {
      default: DESIGN_TOKENS.canvas,
      paper: DESIGN_TOKENS.canvas,
    },
    tokens: DESIGN_TOKENS,
  },
  typography: {
    fontFamily: SANS_BODY,
    // Native h1-h6 kept aligned 1:1 with the named variants below, so existing `variant="h5"`
    // etc. call sites automatically get the fix (sans, not serif) with zero changes.
    h1: { fontFamily: SERIF_DISPLAY, fontSize: 64, fontWeight: 500, lineHeight: 1.05, letterSpacing: -1.5 },
    h2: { fontFamily: SERIF_DISPLAY, fontSize: 48, fontWeight: 500, lineHeight: 1.1, letterSpacing: -1 },
    h3: { fontFamily: SERIF_DISPLAY, fontSize: 36, fontWeight: 500, lineHeight: 1.15, letterSpacing: -0.5 },
    h4: { fontFamily: SERIF_DISPLAY, fontSize: 28, fontWeight: 500, lineHeight: 1.2, letterSpacing: -0.3 },
    h5: { fontFamily: SANS_BODY, fontSize: 22, fontWeight: 500, lineHeight: 1.3, letterSpacing: 0 },
    h6: { fontFamily: SANS_BODY, fontSize: 18, fontWeight: 500, lineHeight: 1.4, letterSpacing: 0 },
    subtitle1: { fontFamily: SANS_BODY, fontSize: 16, fontWeight: 500, lineHeight: 1.4, letterSpacing: 0 }, // title-sm
    body1: { fontFamily: SANS_BODY, fontSize: 16, fontWeight: 400, lineHeight: 1.55 }, // body-md
    body2: { fontFamily: SANS_BODY, fontSize: 14, fontWeight: 400, lineHeight: 1.55 }, // body-sm
    caption: { fontFamily: SANS_BODY, fontSize: 13, fontWeight: 500, lineHeight: 1.4 },
    overline: { fontFamily: SANS_BODY, fontSize: 12, fontWeight: 500, lineHeight: 1.4, letterSpacing: 1.5 }, // caption-uppercase
    button: { fontFamily: SANS_BODY, fontSize: 14, fontWeight: 500, lineHeight: 1, textTransform: 'none' },
    // Named 1:1 with DESIGN.md's own token names — the abstraction to reach for in new code.
    displayXl: { fontFamily: SERIF_DISPLAY, fontSize: 64, fontWeight: 500, lineHeight: 1.05, letterSpacing: -1.5 },
    displayLg: { fontFamily: SERIF_DISPLAY, fontSize: 48, fontWeight: 500, lineHeight: 1.1, letterSpacing: -1 },
    displayMd: { fontFamily: SERIF_DISPLAY, fontSize: 36, fontWeight: 500, lineHeight: 1.15, letterSpacing: -0.5 },
    displaySm: { fontFamily: SERIF_DISPLAY, fontSize: 28, fontWeight: 500, lineHeight: 1.2, letterSpacing: -0.3 },
    titleLg: { fontFamily: SANS_BODY, fontSize: 22, fontWeight: 500, lineHeight: 1.3, letterSpacing: 0 },
    titleMd: { fontFamily: SANS_BODY, fontSize: 18, fontWeight: 500, lineHeight: 1.4, letterSpacing: 0 },
    titleSm: { fontFamily: SANS_BODY, fontSize: 16, fontWeight: 500, lineHeight: 1.4, letterSpacing: 0 },
    bodyMd: { fontFamily: SANS_BODY, fontSize: 16, fontWeight: 400, lineHeight: 1.55 },
    bodySm: { fontFamily: SANS_BODY, fontSize: 14, fontWeight: 400, lineHeight: 1.55 },
    captionUppercase: { fontFamily: SANS_BODY, fontSize: 12, fontWeight: 500, lineHeight: 1.4, letterSpacing: 1.5, textTransform: 'uppercase' },
    code: { fontFamily: MONO_CODE, fontSize: 14, fontWeight: 400, lineHeight: 1.6 },
    navLink: { fontFamily: SANS_BODY, fontSize: 14, fontWeight: 500, lineHeight: 1.4 },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600 },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          displayXl: 'h1', displayLg: 'h2', displayMd: 'h3', displaySm: 'h4',
          titleLg: 'h5', titleMd: 'h6', titleSm: 'p',
          bodyMd: 'p', bodySm: 'p', captionUppercase: 'span', code: 'code', navLink: 'span',
        },
      },
    },
  },
});

export default theme;

// ---------------------------------------------------------------------------
// Non-brand shared colors — NOT part of DESIGN.md's palette (that's DESIGN_TOKENS above), but
// still worth a single source of truth wherever the same colors were being copy-pasted verbatim
// across files. Unlike DESIGN_TOKENS, these are plain constants (imported directly, not via
// useTheme()) since they don't participate in the MUI theme (no dark-mode variant, nothing here
// responds to theme changes) — the hook indirection would be ceremony with no payoff.
// ---------------------------------------------------------------------------

// Certificate-category colors — a categorical color-per-cert-type scheme (Gas/Electrical/Fire/
// etc.), previously copy-pasted identically into 5 files. 3 of those 5 copies (ActiveReminders,
// RemindersTimeline, CertificateRenewalsPage) were missing the "Emergency Lighting Certificate"
// entry entirely, silently falling back to gray for that one category — a real inconsistency this
// consolidation fixes rather than reproduces.
export const CATEGORY_COLORS: Record<string, string> = {
  'Gas Certificate': '#FF6B6B',
  'Electrical Certificate': '#4ECDC4',
  'Fire Certificate': '#FF8A5B',
  'Smoke Certificate': '#95a5a6',
  'Insurance Certificate': '#3498db',
  'Licence Certificate': '#9b59b6',
  'Deposit Protection Certificate': '#27ae60',
  'Energy Certificate': '#f39c12',
  'Emergency Lighting Certificate': '#f97316',
  'Other': '#7f8c8d',
};
export const getCategoryColor = (category: string): string =>
  CATEGORY_COLORS[category] || CATEGORY_COLORS['Other'];

// A handful of other colors that repeated identically across 2+ files (same copy-paste-drift risk
// as CATEGORY_COLORS, just smaller sets). Deliberately NOT exhaustive — genuinely one-off colors
// used in a single file are left as plain literals there, since naming something with exactly one
// consumer doesn't reduce duplication or drift risk.
export const UTILITY_COLORS = {
  comparisonOld: '#374151',        // "before" value text in renewal/upload before-vs-after grids
  comparisonNewStrong: '#065f46',  // "after" value text, same grids
  comparisonNewAccent: '#059669',  // "NEW" badge label, same grids
  errorSoft: '#fef2f2',            // soft red background for error/delete affordances
} as const;
